/* @flow */
/**
 * Utilities for building PPM report field and filter request metadata used by report searches.
 */
import ppmReportsHeader from '../../constants/ppmReportsHeader';
import ppmReportFieldProperties from '../../constants/fieldProperties/ppmReportFieldProperties';

/**
 * Adds default, secondary, additional, and always-requested PPM report fields to the provided field
 * list.
 */
export function getFieldsList(secondaryFields: Object) {
  ppmReportsHeader
    // Default headers drive the initial report field list.
    .filter((header) => header.default)
    .forEach((header) => {
      secondaryFields.push(header.primary);
      if (header.secondary) {
        secondaryFields.push(header.secondary);
      }
      if (header.additionalFields) {
        secondaryFields.push(...header.additionalFields);
      }
    });
  ppmReportsHeader.forEach((headRow: Object) => {
    if (headRow.includeInRequest) {
      secondaryFields.push(headRow.primary);
    }
  });
}

/**
 * Adds filtersInResponse entries for currently selected PPM report filters on an outgoing request
 * body.
 */
export function getRequestedFilterValues(requestBody: Object, state: Object) {
  const requestedBody = requestBody;
  // Request filter buckets only for filters currently active in state.
  requestedBody.filtersInResponse = Object.keys(state.ppmReport.selectedFilters)
    .filter((filter) => state.ppmReport.selectedFilters[filter])
    .map((filter) => {
      const filterInResponse = {};
      const fieldProperties = ppmReportFieldProperties[filter] || {};
      if (!fieldProperties) {
        return null;
      }
      filterInResponse.primary = fieldProperties.primary;
      if (fieldProperties.secondary) {
        filterInResponse.secondary = fieldProperties.secondary;
      }
      return filterInResponse;
    })
    .filter((element) => element);
}

/**
 * Adds filtersInResponse entries from search panel selected filter options to an outgoing request
 * body.
 */
export function getSelectedFilterValues(requestBody: Object, state: Object) {
  const requestedBody = requestBody;
  requestedBody.filtersInResponse = state.searchpaneldata?.selectedFilterOptions?.map((filter) => {
    const filterInResponse = {};
    const fieldProperties = ppmReportFieldProperties[filter] || {};
    if (!fieldProperties) {
      return null;
    }
    filterInResponse.primary = fieldProperties.primary;
    if (fieldProperties.secondary) {
      filterInResponse.secondary = fieldProperties.secondary;
    }
    return filterInResponse;
  });
}

/**
 * Builds API filter descriptors for operational-metrics-selected PPM report filters.
 */
export function getSelectedFilterValuesFromOpMetrics(filterValues: Object) {
  const filtersFromOperationalMetrics = filterValues?.map((filter) => {
    const filterInResponse = {};
    const fieldProperties = ppmReportFieldProperties[filter] || {};
    if (!fieldProperties.primary) {
      return null;
    }
    filterInResponse.primary = fieldProperties.primary;
    if (fieldProperties.secondary) {
      filterInResponse.secondary = fieldProperties.secondary;
    }
    return filterInResponse;
  });
  return filtersFromOperationalMetrics?.filter((element) => element);
}

/**
 * Maps saved filter criteria into filtersInResponse descriptors for a PPM report request.
 */
export function getFiltersInResponseRequestBody(filterData: Object) {
  const filtersInRequestBody = filterData?.map((filter) => {
    const filterInResponse = {};
    const fieldProperties = ppmReportFieldProperties[filter.fieldName] || {};
    if (!fieldProperties) {
      return null;
    }
    filterInResponse.primary = fieldProperties.primary;
    if (fieldProperties.secondary) {
      filterInResponse.secondary = fieldProperties.secondary;
    }
    return filterInResponse;
  });

  return filtersInRequestBody.filter(Boolean);
}

/**
 * Adds filtersInResponse descriptors for the supplied selected filter keys to a PPM report request
 * body.
 */
export function getFiltersForRequest(requestBody: Object, selectedFilterKeys: Object) {
  const requestedBody = requestBody;
  requestedBody.filtersInResponse = selectedFilterKeys.map((filter) => {
    const filterInResponse = {};
    const fieldProperties = ppmReportFieldProperties[filter] || {};
    if (!fieldProperties) {
      return null;
    }
    filterInResponse.primary = fieldProperties.primary;
    if (fieldProperties.secondary) {
      filterInResponse.secondary = fieldProperties.secondary;
    }
    return filterInResponse;
  })
    .filter((element) => element);
}

/**
 * Invokes the optional request-new-filters callback with an error response.
 */
export function asyncCallToNewFiltersCallback(requestNewFiltersCallback: Function, error: Object) {
  if (requestNewFiltersCallback) {
    requestNewFiltersCallback(null, error);
  }
}

/**
 * Keeps selected filter state aligned with the list of filters enabled in the filter bar.
 */
export function getModifiedSelectedFilters(
  filtersSelected: Object,
  filter: string,
  modifiedSelectedFilters: Object
) {
  const modifiedSelectedFiltersNew = modifiedSelectedFilters;
  if (filtersSelected.includes(filter)) {
    if (!modifiedSelectedFilters[filter]) modifiedSelectedFiltersNew[filter] = [];
  } else {
    modifiedSelectedFiltersNew[filter] = null;
  }
}

/**
 * Collects filter descriptors that should be requested or retained based on current dropdown cache
 * state.
 */
export function getSelectedFilterKeys(
  filter: string,
  modifiedSelectedFilters: Object,
  filterDropdownValues: Object,
  filtersToBeRequested: Object,
  filtersInResponse: Object
) {
  // The API accepts a primary field and optional secondary field per filter bucket.
  const filterRequest: { primary: string; secondary?: string; } = { primary: filter };
  if (ppmReportFieldProperties[filter]?.secondary) {
    filterRequest.secondary = ppmReportFieldProperties[filter].secondary;
  }
  if (modifiedSelectedFilters[filter]) {
    if (!filterDropdownValues[filter]) {
      filtersToBeRequested.push(filterRequest);
    }
    filtersInResponse.push(filterRequest);
  }
}
